import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getQueryFn } from "@/lib/queryClient";
import { DataTable, type ColumnDef } from "@/components/shared/data-table";
import { PageHeader } from "@/components/shared/page-header";
import { StatusBadge } from "@/components/shared/status-badge";
import { formatCurrency, formatDate } from "@/lib/format";
import { Package } from "lucide-react";
import type { ClientService, Client, Product } from "@db/schema";

type ServiceWithRelations = ClientService & { client: Client; product: Product };

const columns: ColumnDef<ServiceWithRelations>[] = [
  {
    key: "service",
    header: "Service",
    cell: (row) => (
      <span className="font-medium">
        {row.customName || row.product.name}
      </span>
    ),
  },
  {
    key: "status",
    header: "Status",
    cell: (row) => <StatusBadge type="service" status={row.status} />,
  },
  {
    key: "price",
    header: "Price",
    cell: (row) =>
      formatCurrency(row.price || row.product.defaultPrice, row.product.currency),
  },
  {
    key: "recurrence",
    header: "Recurrence",
    cell: (row) =>
      row.recurrence.charAt(0).toUpperCase() + row.recurrence.slice(1),
  },
  {
    key: "nextRenewalDate",
    header: "Next Renewal",
    cell: (row) => formatDate(row.nextRenewalDate),
  },
];

export default function ClientBilling() {
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery<ServiceWithRelations[]>({
    queryKey: ["/api/portal/services"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const filtered = data?.filter((service) => {
    if (!search) return true;
    const name = (service.customName || service.product.name).toLowerCase();
    return name.includes(search.toLowerCase());
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Services"
        description="View your active services and renewal schedule"
      />

      <DataTable
        columns={columns}
        data={filtered}
        isLoading={isLoading}
        rowKey={(row) => row.id}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search services..."
        emptyIcon={Package}
        emptyTitle="No services"
        emptyDescription="You don't have any active services yet."
      />
    </div>
  );
}
